/*
 * Nacho Books — predictive search.
 *
 * The whole catalog is small, so it is indexed in the browser from
 * /collections/all?view=search-index. That allows accent-insensitive,
 * typo-tolerant and bilingual (English/Spanish) matching that Shopify's
 * native search does not provide. Pages and articles still come from the
 * Shopify predictive search API.
 */
(function () {
  'use strict';

  var configEl = document.getElementById('NachoSearchConfig');
  if (!configEl) return;

  var config;
  try {
    config = JSON.parse(configEl.textContent);
  } catch (error) {
    return;
  }
  var strings = config.strings || {};

  var CACHE_KEY = 'nacho-search-index-v1';
  var CACHE_TTL = 30 * 60 * 1000;
  var RECENT_KEY = 'nacho-search-recent';
  var MAX_PRODUCTS = 6;
  var MAX_COLLECTIONS = 4;

  var STOPWORDS = toSet('de del la las el los y e o u en con por para un una unos unas al lo su sus the of and or for to in on with an is my mi');

  // Each group is a set of equivalent terms (normalized, singular).
  var SYNONYM_GROUPS = [
    'lectura leer lee leo lector reading read reader lecto literacy lectoescritura',
    'escritura escribir escribe escribo writing write writer',
    'caligrafia cursiva cursive handwriting calligraphy letra',
    'matematica mate math maths mathematic numero number counting contar sumar',
    'arte art artistic dibujo dibujar dibuja drawing draw colorear colorea coloring colouring pintar painting',
    'recortable recortar cut cutting cutout tijera scissors manualidad craft',
    'preescolar prescolar preschool kinder kindergarten jardin prejardin transicion toddler aprestamiento',
    'maestro maestra docente profesor profesora teacher educador educator guia guide homeschool aula classroom',
    'cuaderno workbook activity actividad cartilla ejercicio worksheet',
    'libro book',
    'literatura literature novela novel clasico classic cuento story fabula poesia poem',
    'diccionario dictionary',
    'poster cartel afiche',
    'nino nina kid child children infantil',
    'espanol spanish castellano',
    'comprension comprehension',
    'inicial primer first beginner principiante basico'
  ];

  /* ---------- Text utilities ---------- */

  function toSet(words) {
    var set = {};
    words.split(' ').forEach(function (word) { if (word) set[word] = true; });
    return set;
  }

  function normalize(value) {
    return String(value || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .replace(/[^a-z0-9]+/g, ' ')
      .trim();
  }

  function stem(word) {
    if (word.length > 3 && word.charAt(word.length - 1) === 's' && word.charAt(word.length - 2) !== 's') {
      return word.slice(0, -1);
    }
    return word;
  }

  function tokenize(value) {
    var words = normalize(value).split(' ');
    var out = [];
    for (var i = 0; i < words.length; i++) {
      if (words[i]) out.push(stem(words[i]));
    }
    return out;
  }

  function escapeHtml(value) {
    return String(value == null ? '' : value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  // Optimal string alignment distance with an early exit above `max`.
  function editDistance(a, b, max) {
    if (Math.abs(a.length - b.length) > max) return max + 1;
    var prevPrev = null;
    var prev = [];
    var i;
    var j;
    for (j = 0; j <= b.length; j++) prev[j] = j;
    for (i = 1; i <= a.length; i++) {
      var current = [i];
      var rowMin = i;
      for (j = 1; j <= b.length; j++) {
        var cost = a.charAt(i - 1) === b.charAt(j - 1) ? 0 : 1;
        var value = Math.min(prev[j] + 1, current[j - 1] + 1, prev[j - 1] + cost);
        if (prevPrev && i > 1 && j > 1 && a.charAt(i - 1) === b.charAt(j - 2) && a.charAt(i - 2) === b.charAt(j - 1)) {
          value = Math.min(value, prevPrev[j - 2] + 1);
        }
        current[j] = value;
        if (value < rowMin) rowMin = value;
      }
      if (rowMin > max) return max + 1;
      prevPrev = prev;
      prev = current;
    }
    return prev[b.length];
  }

  var synonymsByTerm = {};
  SYNONYM_GROUPS.forEach(function (group) {
    var terms = group.split(' ').map(function (term) { return stem(normalize(term)); });
    terms.forEach(function (term) { synonymsByTerm[term] = terms; });
  });

  /* ---------- Index ---------- */

  var FIELD_WEIGHTS = { title: 10, tags: 5, collections: 4, meta: 2, body: 1 };
  var index = null;
  var indexPromise = null;

  function buildIndex(data) {
    var vocabulary = {};
    var display = {};

    function addTerms(terms, isTitle) {
      terms.forEach(function (term) {
        var entry = vocabulary[term] || (vocabulary[term] = { df: 0, title: false });
        entry.df += 1;
        if (isTitle) entry.title = true;
      });
    }

    function addDisplayWords(text) {
      String(text || '').split(/[\s\/,|()]+/).forEach(function (word) {
        var key = stem(normalize(word));
        if (key && key.indexOf(' ') === -1 && !display[key]) display[key] = word.toLowerCase();
      });
    }

    var products = (data.products || []).map(function (product) {
      var fields = {
        title: tokenize(product.t),
        tags: tokenize((product.tg || []).join(' ') + ' ' + (product.ty || '')),
        collections: tokenize((product.c || []).join(' ')),
        meta: tokenize((product.v || '') + ' ' + (product.s || '')),
        body: tokenize(product.d)
      };
      Object.keys(fields).forEach(function (name) { addTerms(fields[name], name === 'title'); });
      addDisplayWords(product.t);
      addDisplayWords((product.tg || []).join(' '));
      return { item: product, fields: fields, title: normalize(product.t) };
    });

    var collections = (data.collections || []).map(function (collection) {
      var terms = tokenize(collection.t);
      addTerms(terms, true);
      addDisplayWords(collection.t);
      return { item: collection, fields: { title: terms }, title: normalize(collection.t) };
    });

    return { products: products, collections: collections, vocabulary: vocabulary, display: display };
  }

  function fetchIndexPage(page) {
    var url = config.indexUrl + (config.indexUrl.indexOf('?') === -1 ? '?' : '&') + 'page=' + page;
    return fetch(url, { credentials: 'same-origin' }).then(function (response) {
      if (!response.ok) throw new Error('Search index unavailable');
      return response.json();
    });
  }

  function fetchIndex() {
    var merged = { products: [], collections: [] };
    function next(page) {
      return fetchIndexPage(page).then(function (data) {
        merged.products = merged.products.concat(data.products || []);
        if (page === 1) merged.collections = data.collections || [];
        return data.more && page < 10 ? next(page + 1) : merged;
      });
    }
    return next(1);
  }

  function loadIndex() {
    if (index) return Promise.resolve(index);
    if (indexPromise) return indexPromise;

    var cached = null;
    try {
      cached = JSON.parse(sessionStorage.getItem(CACHE_KEY) || 'null');
    } catch (error) {
      cached = null;
    }
    if (cached && cached.locale === config.locale && Date.now() - cached.time < CACHE_TTL) {
      index = buildIndex(cached.data);
      return Promise.resolve(index);
    }

    indexPromise = fetchIndex().then(function (data) {
      try {
        sessionStorage.setItem(CACHE_KEY, JSON.stringify({ time: Date.now(), locale: config.locale, data: data }));
      } catch (error) {
        // Storage can be full or blocked; the index still works in memory.
      }
      index = buildIndex(data);
      return index;
    }).catch(function (error) {
      indexPromise = null;
      throw error;
    });
    return indexPromise;
  }

  /* ---------- Query matching ---------- */

  // Finds the synonym group of a term, tolerating typos ("calligrafy").
  function synonymKey(term) {
    if (synonymsByTerm[term]) return term;
    if (term.length < 5) return null;
    var max = term.length >= 7 ? 2 : 1;
    var best = null;
    var bestDistance = max + 1;
    Object.keys(synonymsByTerm).forEach(function (key) {
      var distance = editDistance(term, key, max);
      if (distance < bestDistance) {
        best = key;
        bestDistance = distance;
      }
    });
    return best;
  }

  function matchTerm(queryTerm, term, groupKey) {
    if (term === queryTerm) return 1;
    var length = queryTerm.length;
    if (length >= 2 && term.indexOf(queryTerm) === 0) {
      return 0.9 - Math.min(0.25, (term.length - length) * 0.02);
    }
    var synonyms = groupKey ? synonymsByTerm[groupKey] : null;
    if (synonyms) {
      for (var i = 0; i < synonyms.length; i++) {
        var synonym = synonyms[i];
        if (synonym === queryTerm) continue;
        if (term === synonym) return 0.8;
        if (synonym.length >= 4 && term.indexOf(synonym) === 0) return 0.72;
      }
    }
    if (length >= 4 && /[a-z]/.test(queryTerm)) {
      var max = length >= 7 ? 2 : 1;
      var distance = editDistance(queryTerm, term, max);
      if (distance <= max) return distance === 1 ? 0.62 : 0.48;
      if (term.length > length && editDistance(queryTerm, term.slice(0, length), 1) <= 1) return 0.45;
    }
    return 0;
  }

  function analyzeQuery(query) {
    var words = normalize(query).split(' ').filter(Boolean);
    return words.map(function (word) {
      var term = stem(word);
      var optional = STOPWORDS[word] === true || (word.length === 1 && !/[0-9]/.test(word));
      var groupKey = optional ? null : synonymKey(term);
      var matches = {};
      var exact = false;
      Object.keys(index.vocabulary).forEach(function (vocabTerm) {
        var score = optional ? (vocabTerm === term ? 1 : 0) : matchTerm(term, vocabTerm, groupKey);
        if (score > 0) {
          matches[vocabTerm] = score;
          // A typo that only reached a synonym group still deserves a "did you mean".
          if (score >= 0.85 || (score >= 0.7 && (!groupKey || groupKey === term))) exact = true;
        }
      });
      return { word: word, term: term, optional: optional, matches: matches, exact: exact };
    });
  }

  function scoreEntry(entry, terms, phrase) {
    var total = 0;
    var required = 0;
    var matched = 0;

    terms.forEach(function (queryTerm) {
      if (!queryTerm.optional) required += 1;
      var best = 0;
      Object.keys(entry.fields).forEach(function (fieldName) {
        var weight = FIELD_WEIGHTS[fieldName];
        var fieldTerms = entry.fields[fieldName];
        for (var i = 0; i < fieldTerms.length; i++) {
          var score = queryTerm.matches[fieldTerms[i]];
          if (score && score * weight > best) best = score * weight;
        }
      });
      if (best > 0) {
        total += best;
        if (!queryTerm.optional) matched += 1;
      }
    });

    if (total === 0) return null;
    if (phrase.length > 2) {
      if (entry.title.indexOf(phrase) === 0) total += 8;
      else if (entry.title.indexOf(phrase) !== -1) total += 5;
    }
    return { entry: entry, score: total, coverage: required ? matched / required : 1 };
  }

  function rank(entries, terms, phrase, strict) {
    var results = [];
    entries.forEach(function (entry) {
      var result = scoreEntry(entry, terms, phrase);
      if (!result) return;
      if (strict && result.coverage < 1) return;
      var score = result.score * result.coverage * result.coverage;
      if (entry.item.a === false) score *= 0.85;
      results.push({ item: entry.item, score: score });
    });
    results.sort(function (a, b) { return b.score - a.score; });
    return results;
  }

  function correctedQuery(terms) {
    var changed = false;
    var words = terms.map(function (queryTerm) {
      if (queryTerm.optional || queryTerm.exact) return queryTerm.word;
      var best = null;
      Object.keys(queryTerm.matches).forEach(function (term) {
        var info = index.vocabulary[term];
        // Prefer the closest spelling; synonyms and popularity only break ties.
        var distance = editDistance(queryTerm.term, term, 3);
        var weight = -distance * 10 + queryTerm.matches[term] * 2 + (info.title ? 2 : 0) + Math.min(info.df, 20) / 10;
        if (!best || weight > best.weight) best = { term: term, weight: weight };
      });
      if (!best) return queryTerm.word;
      changed = true;
      return index.display[best.term] || best.term;
    });
    return changed ? words.join(' ') : null;
  }

  function search(query) {
    var terms = analyzeQuery(query);
    if (!terms.length) return { products: [], collections: [], relaxed: false, correction: null, terms: terms };
    var phrase = normalize(query);
    var products = rank(index.products, terms, phrase, true);
    var relaxed = false;
    if (!products.length) {
      products = rank(index.products, terms, phrase, false);
      relaxed = products.length > 0;
    }
    return {
      products: products.map(function (result) { return result.item; }),
      collections: rank(index.collections, terms, phrase, true).map(function (result) { return result.item; }),
      relaxed: relaxed,
      correction: correctedQuery(terms),
      terms: terms
    };
  }

  function highlight(title, terms) {
    return String(title).split(/(\s+)/).map(function (part) {
      var key = stem(normalize(part));
      if (!key || key.indexOf(' ') !== -1) return escapeHtml(part);
      for (var i = 0; i < terms.length; i++) {
        if (terms[i].matches[key] >= 0.45) return '<mark>' + escapeHtml(part) + '</mark>';
      }
      return escapeHtml(part);
    }).join('');
  }

  /* ---------- Rendering helpers ---------- */

  var optionCounter = 0;

  function optionId() {
    optionCounter += 1;
    return 'NachoSearchOption-' + optionCounter;
  }

  function fill(template, key, value) {
    return String(template || '').split(key).join(value);
  }

  function searchUrl(query) {
    return config.searchUrl + '?q=' + encodeURIComponent(query) + '&options%5Bprefix%5D=last';
  }

  function productCard(product, terms, asOption) {
    var price = product.cp
      ? '<s>' + escapeHtml(product.cp) + '</s> <span class="nacho-search__price--sale">' + escapeHtml(product.p) + '</span>'
      : escapeHtml(product.p);
    var badge = product.a === false ? '<span class="nacho-search__badge">' + escapeHtml(strings.soldOut) + '</span>' : '';
    var meta = [product.ty, product.v].filter(Boolean).map(escapeHtml).join(' · ');
    var image = product.i
      ? '<img src="' + escapeHtml(product.i) + '" alt="" width="64" height="64" loading="lazy">'
      : '<span class="nacho-search__placeholder" aria-hidden="true"></span>';
    var link = '<a class="nacho-search__product" href="' + escapeHtml(product.u) + '"' + (asOption ? ' tabindex="-1"' : '') + '>' +
      '<span class="nacho-search__thumb">' + image + '</span>' +
      '<span class="nacho-search__info"><span class="nacho-search__title">' + highlight(product.t, terms) + '</span>' +
      (meta ? '<span class="nacho-search__meta">' + meta + '</span>' : '') + '</span>' +
      '<span class="nacho-search__price">' + price + badge + '</span></a>';
    return asOption ? '<li role="option" id="' + optionId() + '" aria-selected="false">' + link + '</li>' : '<li>' + link + '</li>';
  }

  function featuredCard(product) {
    var image = product.i
      ? '<img src="' + escapeHtml(product.i) + '" alt="" width="120" height="120" loading="lazy">'
      : '<span class="nacho-search__placeholder" aria-hidden="true"></span>';
    return '<li role="option" id="' + optionId() + '" aria-selected="false"><a class="nacho-search__featured-card" href="' + escapeHtml(product.u) + '" tabindex="-1">' +
      '<span class="nacho-search__featured-img">' + image + '</span>' +
      '<span class="nacho-search__featured-title">' + escapeHtml(product.t) + '</span>' +
      '<span class="nacho-search__featured-price">' + escapeHtml(product.p) + '</span></a></li>';
  }

  // "Not sure which book?" card linking to the home quiz
  function quizCard() {
    var quiz = config.quiz;
    if (!quiz || !quiz.url) return '';
    return '<a class="nacho-search__quiz" href="' + escapeHtml(quiz.url) + '" data-nacho-search-close-on-click>' +
      '<span class="nacho-search__quiz-text"><strong>' + escapeHtml(quiz.title) + '</strong><span>' + escapeHtml(quiz.text) + '</span></span>' +
      '<span class="nacho-search__quiz-cta">' + escapeHtml(quiz.cta) + ' →</span></a>';
  }

  function featuredGroup() {
    if (!config.featured || !config.featured.length) return '';
    return group('NachoSearchFeatured', strings.featured, config.featured.map(featuredCard).join(''), 'nacho-search__featured');
  }

  function chip(label, url) {
    return '<li role="option" id="' + optionId() + '" aria-selected="false"><a class="nacho-search__chip" href="' + escapeHtml(url) + '" tabindex="-1">' + escapeHtml(label) + '</a></li>';
  }

  function group(id, heading, items, className) {
    if (!items) return '';
    return '<div class="nacho-search__group" role="group" aria-labelledby="' + id + '">' +
      '<p class="nacho-search__heading" id="' + id + '" role="presentation">' + escapeHtml(heading) + '</p>' +
      '<ul class="' + className + '" role="presentation">' + items + '</ul></div>';
  }

  /* ---------- Recent searches ---------- */

  function readRecent() {
    try {
      var value = JSON.parse(localStorage.getItem(RECENT_KEY) || '[]');
      return Array.isArray(value) ? value.slice(0, 5) : [];
    } catch (error) {
      return [];
    }
  }

  function saveRecent(query) {
    var clean = String(query || '').trim();
    if (clean.length < 2) return;
    var list = readRecent().filter(function (item) { return normalize(item) !== normalize(clean); });
    list.unshift(clean);
    try {
      localStorage.setItem(RECENT_KEY, JSON.stringify(list.slice(0, 5)));
    } catch (error) {
      // Ignore: recent searches are a convenience only.
    }
  }

  /* ---------- Dialog ---------- */

  var dialog = document.getElementById('NachoSearch');

  if (dialog) {
    // The header creates its own stacking context (z-index), so the dialog must
    // live directly under <body> to sit above hero sections and sliders.
    if (dialog.parentNode !== document.body) document.body.appendChild(dialog);

    var form = dialog.querySelector('form');
    var input = dialog.querySelector('[data-nacho-search-input]');
    var results = dialog.querySelector('[data-nacho-search-results]');
    var status = dialog.querySelector('[data-nacho-search-status]');
    var clearButton = dialog.querySelector('[data-nacho-search-clear]');
    var lastTrigger = null;
    var activeIndex = -1;
    var debounceTimer = null;
    var remoteTimer = null;
    var remoteController = null;
    var renderToken = 0;
    var TRIGGERS = '[data-nacho-search-open], #open-search-popup, [data-toggle="search-box"], [data-searchicon]';

    var setStatus = function (text) {
      status.textContent = text || '';
    };

    var options = function () {
      return Array.prototype.slice.call(results.querySelectorAll('[role="option"]'));
    };

    var setActive = function (indexValue) {
      var list = options();
      list.forEach(function (option) {
        option.setAttribute('aria-selected', 'false');
        option.classList.remove('is-active');
      });
      if (!list.length || indexValue < 0) {
        activeIndex = -1;
        input.removeAttribute('aria-activedescendant');
        return;
      }
      activeIndex = (indexValue + list.length) % list.length;
      var option = list[activeIndex];
      option.setAttribute('aria-selected', 'true');
      option.classList.add('is-active');
      input.setAttribute('aria-activedescendant', option.id);
      option.scrollIntoView({ block: 'nearest' });
    };

    var renderEmptyState = function () {
      var html = '';
      var recent = readRecent();
      if (recent.length) {
        html += group('NachoSearchRecent', strings.recent, recent.map(function (item) { return chip(item, searchUrl(item)); }).join(''), 'nacho-search__chips');
      }
      if (config.popular && config.popular.length) {
        html += group('NachoSearchPopular', strings.popular, config.popular.map(function (item) { return chip(item, searchUrl(item)); }).join(''), 'nacho-search__chips');
      }
      html += featuredGroup();
      html += quizCard();
      if (index && index.collections.length) {
        var collections = index.collections.slice().sort(function (a, b) { return b.item.n - a.item.n; });
        html += group('NachoSearchCategories', strings.categories, collections.map(function (entry) { return chip(entry.item.t, entry.item.u); }).join(''), 'nacho-search__chips');
      }
      results.innerHTML = html;
      input.setAttribute('aria-expanded', html ? 'true' : 'false');
      setActive(-1);
      setStatus('');
    };

    var renderRemote = function (query, token) {
      if (!config.predictiveUrl) return;
      if (remoteController) remoteController.abort();
      remoteController = typeof AbortController === 'function' ? new AbortController() : null;
      var url = config.predictiveUrl + '?q=' + encodeURIComponent(query) +
        '&resources%5Btype%5D=page,article&resources%5Blimit%5D=3';
      fetch(url, { credentials: 'same-origin', signal: remoteController ? remoteController.signal : undefined })
        .then(function (response) { return response.ok ? response.json() : null; })
        .then(function (data) {
          if (!data || token !== renderToken) return;
          var found = data.resources && data.resources.results ? data.resources.results : {};
          var items = (found.pages || []).concat(found.articles || []);
          if (!items.length) return;
          var html = items.map(function (item) {
            return '<li role="option" id="' + optionId() + '" aria-selected="false"><a class="nacho-search__link" href="' + escapeHtml(item.url) + '" tabindex="-1">' + escapeHtml(item.title) + '</a></li>';
          }).join('');
          var footer = results.querySelector('.nacho-search__footer');
          var wrapper = document.createElement('div');
          wrapper.innerHTML = group('NachoSearchPages', strings.pages, html, 'nacho-search__links');
          if (footer) results.insertBefore(wrapper.firstChild, footer);
          else results.appendChild(wrapper.firstChild);
        })
        .catch(function () {
          // Pages and articles are optional; product results already rendered.
        });
    };

    var renderResults = function (query) {
      var token = ++renderToken;
      var outcome = search(query);
      var html = '';

      if (outcome.correction && normalize(outcome.correction) !== normalize(query)) {
        html += '<p class="nacho-search__notice">' + fill(strings.showingFor, '__TERMS__', '<a href="' + escapeHtml(searchUrl(outcome.correction)) + '">' + escapeHtml(outcome.correction) + '</a>') + '</p>';
      } else if (outcome.relaxed) {
        html += '<p class="nacho-search__notice">' + escapeHtml(strings.similar) + '</p>';
      }

      if (outcome.collections.length) {
        html += group('NachoSearchCollections', strings.categories, outcome.collections.slice(0, MAX_COLLECTIONS).map(function (item) {
          return chip(item.t, item.u);
        }).join(''), 'nacho-search__chips');
      }

      if (outcome.products.length) {
        html += group('NachoSearchProducts', strings.products, outcome.products.slice(0, MAX_PRODUCTS).map(function (item) {
          return productCard(item, outcome.terms, true);
        }).join(''), 'nacho-search__products');
      } else {
        html += '<p class="nacho-search__empty">' + fill(strings.noResults, '__TERMS__', escapeHtml(query)) + '</p>';
        html += quizCard();
        html += featuredGroup();
      }

      html += '<div class="nacho-search__footer"><a class="nacho-search__all" role="option" aria-selected="false" id="' + optionId() + '" href="' + escapeHtml(searchUrl(query)) + '" tabindex="-1">' +
        fill(strings.viewAll, '__TERMS__', escapeHtml(query)) + '</a></div>';

      results.innerHTML = html;
      input.setAttribute('aria-expanded', 'true');
      setActive(-1);
      var count = outcome.products.length;
      setStatus(fill(count === 1 ? strings.countOne : strings.countOther, '__COUNT__', count));

      clearTimeout(remoteTimer);
      remoteTimer = setTimeout(function () { renderRemote(query, token); }, 250);
    };

    var update = function () {
      var query = input.value.trim();
      clearButton.hidden = !input.value;
      if (!query) {
        renderToken += 1;
        renderEmptyState();
        return;
      }
      if (!index) {
        setStatus(strings.loading);
        loadIndex().then(function () {
          if (input.value.trim() === query) renderResults(query);
        }).catch(function () {
          setStatus('');
        });
        return;
      }
      renderResults(query);
    };

    var open = function (trigger) {
      if (!dialog.hidden) return;
      lastTrigger = trigger || document.activeElement;
      dialog.hidden = false;
      document.documentElement.classList.add('nacho-search-open');
      if (lastTrigger && lastTrigger.setAttribute) lastTrigger.setAttribute('aria-expanded', 'true');
      input.focus();
      update();
      loadIndex().then(function () {
        if (!input.value.trim()) renderEmptyState();
      }).catch(function () {});
    };

    var close = function () {
      if (dialog.hidden) return;
      dialog.hidden = true;
      document.documentElement.classList.remove('nacho-search-open');
      input.setAttribute('aria-expanded', 'false');
      if (lastTrigger && lastTrigger.setAttribute) lastTrigger.setAttribute('aria-expanded', 'false');
      if (lastTrigger && typeof lastTrigger.focus === 'function') lastTrigger.focus();
    };

    // Capture phase so the legacy Kala/Foundation search toggles never fire.
    document.addEventListener('click', function (event) {
      var trigger = event.target.closest && event.target.closest(TRIGGERS);
      if (!trigger || dialog.contains(trigger)) return;
      event.preventDefault();
      event.stopPropagation();
      open(trigger);
    }, true);

    ['pointerenter', 'focusin', 'touchstart'].forEach(function (type) {
      document.addEventListener(type, function (event) {
        if (event.target.closest && event.target.closest(TRIGGERS)) loadIndex().catch(function () {});
      }, { capture: true, passive: true });
    });

    dialog.addEventListener('click', function (event) {
      if (event.target === dialog || event.target.closest('[data-nacho-search-close]') || event.target.closest('[data-nacho-search-close-on-click]')) close();
    });

    clearButton.addEventListener('click', function () {
      input.value = '';
      input.focus();
      update();
    });

    input.addEventListener('input', function () {
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(update, 90);
    });

    input.addEventListener('keydown', function (event) {
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        setActive(activeIndex + 1);
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        setActive(activeIndex - 1);
      } else if (event.key === 'Enter' && activeIndex > -1) {
        var option = options()[activeIndex];
        var link = option && (option.matches('a') ? option : option.querySelector('a'));
        if (link) {
          event.preventDefault();
          saveRecent(input.value);
          window.location.href = link.href;
        }
      }
    });

    dialog.addEventListener('keydown', function (event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        close();
      } else if (event.key === 'Tab') {
        var focusable = Array.prototype.slice.call(dialog.querySelectorAll('input, button:not([hidden]), [href]:not([tabindex="-1"])'));
        if (!focusable.length) return;
        var first = focusable[0];
        var last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }
    });

    results.addEventListener('click', function (event) {
      if (event.target.closest('a')) saveRecent(input.value);
    });

    form.addEventListener('submit', function (event) {
      if (!input.value.trim()) {
        event.preventDefault();
        return;
      }
      saveRecent(input.value);
    });
  }

  /* ---------- Search results page fallback ---------- */

  var fallback = document.querySelector('[data-nacho-search-fallback]');
  if (fallback) {
    var terms = fallback.getAttribute('data-terms') || '';
    var serverCount = parseInt(fallback.getAttribute('data-count'), 10) || 0;
    if (terms.trim() && serverCount < 4) {
      loadIndex().then(function () {
        var shown = {};
        document.querySelectorAll('[data-search-results] a[href*="/products/"]').forEach(function (link) {
          shown[link.pathname] = true;
        });
        var outcome = search(terms);
        var extra = outcome.products.filter(function (product) { return !shown[product.u]; }).slice(0, 8);
        if (!extra.length && !outcome.collections.length) return;
        var heading = serverCount === 0 ? strings.similar : strings.alsoLike;
        if (serverCount === 0 && outcome.correction) {
          heading = fill(strings.showingFor, '__TERMS__', escapeHtml(outcome.correction));
        } else {
          heading = escapeHtml(heading);
        }
        var html = '<h2 class="nacho-search-page__subtitle">' + heading + '</h2>';
        if (outcome.collections.length) {
          html += '<ul class="nacho-search__chips">' + outcome.collections.slice(0, MAX_COLLECTIONS).map(function (item) {
            return '<li><a class="nacho-search__chip" href="' + escapeHtml(item.u) + '">' + escapeHtml(item.t) + '</a></li>';
          }).join('') + '</ul>';
        }
        if (extra.length) {
          html += '<ul class="nacho-search__products nacho-search__products--grid">' + extra.map(function (item) {
            return productCard(item, outcome.terms, false);
          }).join('') + '</ul>';
        }
        fallback.innerHTML = html;
        fallback.hidden = false;
      }).catch(function () {});
    }
  }
})();
